import { useQueries } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  AUTO_SUGGEST_PERMISSION,
  CREATE_BINDING_PERMISSION,
  can,
  useIdentity,
} from '@/entities/session';
import { nameGlobMatches } from '@/features/catalog-browser';
import {
  AutoSuggestButton,
  AutoSuggestPanel,
  useAutoSuggestPreview,
} from '@/features/rulegen';
import { useConnections } from '@/features/connections';
import { runsLabel, useBindingScheduling } from '@/features/scheduling';
import { ageText } from '@/shared/model/age';
import { Button } from '@/shared/ui/Button';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { HelpLabel } from '@/shared/ui/InfoTip';
import { Modal } from '@/shared/ui/Modal';
import { UnavailableState } from '@/shared/ui/States';

import {
  ruleVersionsQuery,
  useBindings,
  useDisableBinding,
  useExecutions,
  useRequestExecution,
  useRules,
} from '../api/rules';
import type { RuleBinding } from '../api/rules';
import { BindRuleForm } from './BindRuleForm';
import { RunBindingButton } from './RunBindingButton';

/* The rules that measure one dataset, as the design's Rules tab on a dataset: what each checks, on
 * which columns, how severe a failure is, when it runs, when it last ran and whether it is on — with
 * Run, Unbind and Bind a rule where the reader is.
 *
 * **Matched on `name_glob`, and the header says so.** `core.rules` owns selector resolution and
 * matches tags and column roles too, and no route answers "the bindings for this dataset", so this
 * can under-report. The note beside the count is what stops the list reading as the whole truth.
 *
 * **Bind a rule is offered only to a role that may create a binding**, and shown as a dashed,
 * role-gated chip otherwise, as the design does: a reader learns the action exists and why it is not
 * theirs, rather than meeting a 403.
 *
 * **Run all runs exactly the rules listed, on this dataset only.** It sends the listed bindings' ids
 * and this dataset's id rather than omitting `binding_ids`, whose contract meaning is "every enabled
 * binding" — that would also run bindings matched by tag or column role, which this panel says it
 * may not show, so the button would do more than the rows it is counted from. A binding that is off,
 * or waiting on a rule that is not active, is left out for the reason each row's own Run is absent.
 */

/* How many distinct bound rules this panel looks up versions for. The set is the rules bound to one
 * dataset, a handful in every realistic estate; past the cap a row's columns read as unresolved,
 * which is the honest degradation. */
const MAX_RULE_LOOKUPS = 12;
/* Versions are immutable once published, so a long stale time asks nothing whose answer can move. */
const RULE_VERSION_STALE_MS = 5 * 60 * 1000;

export function DatasetRulesPanel({
  datasetId,
  qualified,
  connectionId,
}: {
  readonly datasetId: string;
  /** The dataset's qualified name, which is what a `name_glob` selector is matched against. */
  readonly qualified: string;
  /** The dataset's source. Auto Suggest's "draft in Suggestions" link opens that screen on it. */
  readonly connectionId?: string | undefined;
}): ReactElement {
  const { t } = useTranslation();
  const identity = useIdentity();
  const mayBind = can(identity.data, CREATE_BINDING_PERMISSION);
  const mayAutoSuggest = can(identity.data, AUTO_SUGGEST_PERMISSION);
  const autoSuggest = useAutoSuggestPreview();
  const bindings = useBindings();
  const rules = useRules('');
  const executions = useExecutions();
  const disable = useDisableBinding();
  const runAll = useRequestExecution();
  const navigate = useNavigate();
  const [binding, setBinding] = useState(false);
  const [unbinding, setUnbinding] = useState<RuleBinding | null>(null);

  const bound = useMemo(
    () =>
      (bindings.data?.items ?? []).filter((each) =>
        each.selectors.some(
          (selector) =>
            selector.kind === 'name_glob' &&
            /* `pattern` is optional on the wire — a `tag` selector carries none. */
            typeof selector.pattern === 'string' &&
            nameGlobMatches(selector.pattern, qualified),
        ),
      ),
    [bindings.data, qualified],
  );
  const rulesById = new Map((rules.data?.items ?? []).map((rule) => [rule.id, rule]));
  const scheduling = useBindingScheduling(bound.map((each) => each.id));
  const connections = useConnections();

  /* The columns a rule reads live on the *version*, so one read per distinct bound rule, bounded and
     sharing the rule screen's cache key. */
  const ruleIds = [...new Set(bound.map((each) => each.rule_id))].slice(
    0,
    MAX_RULE_LOOKUPS,
  );
  const versions = useQueries({
    queries: ruleIds.map((ruleId) => ({
      ...ruleVersionsQuery(ruleId),
      retry: false,
      staleTime: RULE_VERSION_STALE_MS,
    })),
  });
  const columnsByVersion = new Map<string, readonly string[]>();
  for (const result of versions) {
    for (const version of result.data?.items ?? []) {
      columnsByVersion.set(version.id, version.expression.columns);
    }
  }

  const now = new Date();
  const lastRunOf = (each: RuleBinding): string | null => {
    /* The newest execution that measured this dataset with this binding's version. Executions
       arrive newest first, so the first match is the latest. */
    const run = (executions.data?.items ?? []).find(
      (execution) =>
        execution.started_at !== null &&
        execution.targets.some(
          (target) =>
            target.dataset_id === datasetId &&
            target.rule_version_id === each.rule_version_id,
        ),
    );
    return run?.started_at ?? null;
  };

  /* Off is off, whatever the rule says; on behind a rule that is not active is waiting on that rule,
     which is the commonest "why does nothing run". */
  const stateOf = (each: RuleBinding): 'enabled' | 'disabled' | 'waiting' => {
    const rule = rulesById.get(each.rule_id);
    return !each.enabled
      ? 'disabled'
      : rule !== undefined && rule.state !== 'active'
        ? 'waiting'
        : 'enabled';
  };
  const runnable = bound.filter((each) => stateOf(each) === 'enabled');

  const runAllButton =
    runnable.length > 0 ? (
      <Button
        tone="secondary"
        busy={runAll.isPending}
        title={t('dataset.rules.runAllMeaning')}
        onClick={() => {
          runAll.mutate(
            { bindingIds: runnable.map((each) => each.id), datasetIds: [datasetId] },
            {
              /* To the run, as each row's Run does: a 202's tasks have not started, and the page
                 that shows them arriving is the useful next screen. */
              onSuccess: (execution) => {
                navigate(`/executions/${execution.id}`);
              },
            },
          );
        }}
      >
        {t('dataset.rules.runAll', { count: runnable.length })}
      </Button>
    ) : null;

  /* Auto Suggest first, as the brief orders the toolbar: it is the action that does the most of
     the work. Gated on the permission its service checks, so a reader is not offered a button that
     answers 403. */
  const autoSuggestButton = mayAutoSuggest ? (
    <AutoSuggestButton
      busy={autoSuggest.isPending}
      onRun={() => {
        autoSuggest.mutate(datasetId);
      }}
    />
  ) : null;
  /* Only this dataset's recommendations: the panel is remounted per dataset today, and the guard
     keeps the list honest if that ever stops being true. */
  const autoSuggestOutcome =
    autoSuggest.variables === datasetId ? (
      <AutoSuggestPanel
        datasetId={datasetId}
        connectionId={connectionId}
        preview={autoSuggest.data}
        loading={autoSuggest.isPending}
        error={autoSuggest.error}
        onDismiss={() => {
          autoSuggest.reset();
        }}
      />
    ) : null;

  const bindButton = mayBind ? (
    <button
      type="button"
      className="lq-btn lq-btn--primary"
      onClick={() => {
        setBinding(true);
      }}
    >
      {t('rules.bindings.create.open')}
    </button>
  ) : null;

  return (
    <article className="lq-drp">
      <div className="lq-drp__head">
        <div className="lq-drp__heading">
          <HelpLabel subject={t('dataset.rules.title')} help={t('help.feature.rules')}>
            <h2 className="lq-drp__title">{t('dataset.rules.title')}</h2>
          </HelpLabel>
          <p className="lq-drp__sub">
            {bindings.isSuccess
              ? `${t('dataset.rules.count', { count: bound.length })} · ${t('dataset.rules.note')}`
              : t('dataset.rules.note')}
          </p>
        </div>
        <div className="lq-drp__headActions">
          {autoSuggestButton}
          {runAllButton}
          {bindButton ?? (
            <span
              className="lq-gated"
              title={t('dataset.rules.gatedTitle', {
                permission: CREATE_BINDING_PERMISSION,
              })}
            >
              {t('dataset.rules.gated')}
            </span>
          )}
        </div>
      </div>
      {runAll.isError ? (
        <p className="lq-drp__runError" role="alert">
          {t('dataset.rules.runAllRefused', { reason: runAll.error.message })}
        </p>
      ) : null}
      {autoSuggestOutcome}

      {bindings.isPending ? (
        <span
          className="lq-skeleton__bar lq-drp__loading"
          role="status"
          aria-label={t('dataset.rules.loading')}
        />
      ) : bindings.isError ? (
        /* A failed read says so, rather than reading as "no rules on this table". */
        <UnavailableState
          title={t('dataset.rules.unreadable')}
          guidance={bindings.error.message}
        />
      ) : bound.length === 0 ? (
        <div className="lq-drp__none">
          <p className="lq-drp__noneTitle">{t('dataset.rules.none')}</p>
          {bindButton}
        </div>
      ) : (
        <div className="lq-drp__scroll">
          <table className="lq-drp__table">
            <thead>
              <tr>
                <th scope="col">{t('dataset.rules.column.rule')}</th>
                <th scope="col">{t('dataset.rules.column.checks')}</th>
                <th scope="col">{t('dataset.rules.column.columns')}</th>
                <th scope="col">{t('dataset.rules.column.severity')}</th>
                <th scope="col">{t('dataset.rules.column.schedule')}</th>
                <th scope="col">{t('dataset.rules.column.lastRun')}</th>
                <th scope="col">{t('dataset.rules.column.state')}</th>
                <th scope="col">
                  <span className="lq-visually-hidden">
                    {t('rules.bindings.column.actions')}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {bound.map((each) => {
                const rule = rulesById.get(each.rule_id);
                const summary = scheduling.isSuccess
                  ? scheduling.data.get(each.id)
                  : undefined;
                const runs =
                  summary === undefined
                    ? null
                    : runsLabel(
                        t,
                        summary,
                        (id) =>
                          connections.data?.items.find((each) => each.id === id)?.name ??
                          null,
                      );
                const scheduled =
                  summary !== undefined &&
                  ((summary.schedule.configured && summary.schedule.enabled) ||
                    (summary.trigger.configured && summary.trigger.enabled));
                const last = lastRunOf(each);
                const explicit = each.explicit_columns;
                const declared = columnsByVersion.get(each.rule_version_id);
                const columns = explicit.length > 0 ? explicit : (declared ?? null);
                const state = stateOf(each);
                const pattern =
                  each.selectors.find((selector) => selector.kind === 'name_glob')
                    ?.pattern ?? '';
                return (
                  <tr key={each.id}>
                    <td>
                      <div className="lq-drp__rule">
                        {rule?.name ?? (
                          <span className="lq-muted" title={each.rule_id}>
                            {t('dataset.rules.unnamed')}
                          </span>
                        )}
                      </div>
                      <div className="lq-drp__by">
                        {t('dataset.rules.matchedBy', { pattern })}
                      </div>
                    </td>
                    <td className="lq-drp__dim">
                      {rule === undefined ? '—' : t(`rules.kind.${rule.kind}`)}
                    </td>
                    <td className="lq-drp__cols">
                      {columns === null ? (
                        <span className="lq-muted">
                          {t('dataset.rules.columnsUnknown')}
                        </span>
                      ) : columns.length === 0 ? (
                        <span className="lq-muted">{t('dataset.rules.everyColumn')}</span>
                      ) : (
                        columns.join(', ')
                      )}
                    </td>
                    <td className="lq-drp__dim">
                      {t(`rules.severity.${each.severity}.label`)}
                    </td>
                    <td
                      className="lq-drp__when"
                      data-quiet={scheduled ? undefined : 'true'}
                      title={runs?.full}
                    >
                      {runs === null ? t('rules.runs.unread') : runs.short}
                    </td>
                    <td
                      className="lq-drp__when"
                      data-quiet={last === null ? 'true' : undefined}
                    >
                      {last === null
                        ? t('dataset.rules.neverRun')
                        : (ageText(t, last, now) ?? last)}
                    </td>
                    <td>
                      <span className={`lq-tag lq-tag--ru-b${state}`}>
                        <span className="lq-tag__dot" aria-hidden="true" />
                        {state === 'waiting' && rule !== undefined
                          ? t(`rules.bindings.state.rule.${rule.state}`)
                          : t(`rules.bindings.state.${state}`)}
                      </span>
                    </td>
                    <td className="lq-drp__actions">
                      {each.enabled ? (
                        <>
                          {/* Scoped to this dataset as well as to this binding: a name glob can
                              resolve to hundreds of tables, and Run on one dataset's page must not
                              scan the estate. */}
                          <RunBindingButton
                            bindingId={each.id}
                            enabled
                            datasetId={datasetId}
                          />
                          <button
                            type="button"
                            className="lq-ru__small lq-ru__small--danger"
                            onClick={() => {
                              setUnbinding(each);
                            }}
                          >
                            {t('rules.bindings.unbind')}
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={binding}
        title={t('rules.bindings.create.title')}
        hint={t('rules.bindings.create.datasetHint')}
        className="lq-bindm"
        onClose={() => {
          setBinding(false);
        }}
      >
        <BindRuleForm
          initialTarget={qualified}
          onCreated={() => {
            setBinding(false);
          }}
          onCancel={() => {
            setBinding(false);
          }}
        />
      </Modal>

      <ConfirmDialog
        open={unbinding !== null}
        title={t('rules.bindings.unbindConfirm.title')}
        message={
          unbinding === null
            ? ''
            : t('rules.bindings.unbindConfirm.body', {
                rule:
                  rulesById.get(unbinding.rule_id)?.name ??
                  t('rules.bindings.ruleUnknown'),
                target: qualified,
              })
        }
        confirmLabel={t('rules.bindings.unbind')}
        cancelLabel={t('common.cancel')}
        tone="danger"
        busy={disable.isPending}
        error={disable.isError ? disable.error.message : undefined}
        onConfirm={() => {
          if (unbinding === null) return;
          disable.mutate(
            { bindingId: unbinding.id, reason: 'Unbound from the dataset screen' },
            {
              onSuccess: () => {
                setUnbinding(null);
              },
            },
          );
        }}
        onCancel={() => {
          setUnbinding(null);
          disable.reset();
        }}
      />
    </article>
  );
}
